import { Grid } from '@paintscout/ui'
import { KeypadInputField, useClientOptions, DateRangePicker } from '@paintscout/ui-business'
import { getFeatures, getObjectLabels } from '@paintscout/util/builder'
import type { ValueFilter } from 'json-to-lucene'
import moment from 'moment'
import React from 'react'
import { handleRangeFilterChange } from '@paintscout/util/util'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface LocationSearchFiltersProps {
  classes?: { root?: string }
  filters: ValueFilter[]
  onChange?: (filters: ValueFilter[]) => any
}

const FILTER_USER_COUNT = 'userCounts.all'
const FILTER_TOTAL_QUOTES = 'aggregates.totalQuotes'
const FILTER_CLOSED_VALUE = 'aggregates.closedValue'
const FILTER_CLOSE_RATE = 'aggregates.closeRate'

export default function LocationSearchFilters({ filters, onChange }: LocationSearchFiltersProps) {
  const { options } = useClientOptions()

  const { quote: quoteLabel, currency } = getObjectLabels({ options })

  const { crm: crmFlag } = useFlags()
  const features = getFeatures({ options })
  const tagsEnabled = !!(features.quotes?.tags?.enabled || crmFlag)

  const dateFilter = filters.find((filter) => filter.id === 'created')
  const userCountFilter = filters.find((filter) => filter.id === FILTER_USER_COUNT)
  const quoteCountFilter = filters.find((filter) => filter.id === FILTER_TOTAL_QUOTES)
  const dollarsWonFilter = filters.find((filter) => filter.id === FILTER_CLOSED_VALUE)
  const winRateFilter = filters.find((filter) => filter.id === FILTER_CLOSE_RATE)

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid container alignItems="flex-end" item xs={12} lg={tagsEnabled ? 3 : 4}>
        <Grid item xs={6} style={{ paddingRight: 4 }}>
          <KeypadInputField
            label={`Users #`}
            margin="none"
            placeholder="Min"
            format="quantity"
            fullWidth
            value={userCountFilter?.value?.[0] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_USER_COUNT, start: value, filters, onChange })
            }}
          />
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: 4 }}>
          <KeypadInputField
            margin="none"
            flipX
            placeholder="Max"
            format="quantity"
            value={userCountFilter?.value?.[1] ?? ''}
            fullWidth
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_USER_COUNT, end: value, filters, onChange })
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={dateFilter?.value[0] ? moment(dateFilter.value[0]) : null}
          endDate={dateFilter?.value[1] ? moment(dateFilter.value[1]) : null}
          onConfirm={(startDate, endDate) => {
            handleRangeFilterChange({
              id: 'created',
              start: startDate ? startDate.set('hour', 0).unix() * 1000 : null,
              end: endDate ? endDate.set('hour', 23).unix() * 1000 : null,
              filters,
              onChange
            })
          }}
          PopperProps={{
            placement: 'bottom-end'
          }}
        />
      </Grid>
      <Grid container alignItems="flex-end" item xs={12} lg={tagsEnabled ? 3 : 4}>
        <Grid item xs={6} style={{ paddingRight: 4 }}>
          <KeypadInputField
            label={`${quoteLabel.value} #`}
            margin="none"
            placeholder="Min"
            format="quantity"
            fullWidth
            value={quoteCountFilter?.value?.[0] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_TOTAL_QUOTES, start: value, filters, onChange })
            }}
          />
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: 4 }}>
          <KeypadInputField
            margin="none"
            flipX
            placeholder="Max"
            format="quantity"
            value={quoteCountFilter?.value?.[1] ?? ''}
            fullWidth
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_TOTAL_QUOTES, end: value, filters, onChange })
            }}
          />
        </Grid>
      </Grid>
      <Grid container alignItems="flex-end" item xs={12} lg={tagsEnabled ? 3 : 4}>
        <Grid item xs={6} style={{ paddingRight: 4 }}>
          <KeypadInputField
            label={`Dollars Won (${currency.symbol})`}
            margin="none"
            placeholder="Min"
            format="dimension"
            fullWidth
            value={dollarsWonFilter?.value?.[0] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_CLOSED_VALUE, start: value, filters, onChange })
            }}
          />
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: 4 }}>
          <KeypadInputField
            margin="none"
            flipX
            placeholder="Max"
            format="dimension"
            value={dollarsWonFilter?.value?.[1] ?? ''}
            fullWidth
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_CLOSED_VALUE, end: value, filters, onChange })
            }}
          />
        </Grid>
      </Grid>
      <Grid container alignItems="flex-end" item xs={12} lg={tagsEnabled ? 3 : 4}>
        <Grid item xs={6} style={{ paddingRight: 4 }}>
          <KeypadInputField
            label={`Win Rate (%)`}
            margin="none"
            placeholder="Min"
            format="dimension"
            fullWidth
            value={winRateFilter?.value?.[0] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_CLOSE_RATE, start: value, filters, onChange })
            }}
          />
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: 4 }}>
          <KeypadInputField
            margin="none"
            flipX
            placeholder="Max"
            format="dimension"
            value={winRateFilter?.value?.[1] ?? ''}
            fullWidth
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_CLOSE_RATE, end: value, filters, onChange })
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
